import * as React from 'react'
import * as Router from 'react-router-dom'

import ContentEditable from 'react-contenteditable'
import supportsWebP from 'supports-webp'

import { createRenderer, PostAnchors, PostContent, PostFootNotes } from '../api/content'
import { createPost, Draft, Post, toCSTDateString, useEditPost, usePost } from '../api/request'

import { Button } from './Button'
import { PostArticleLink } from './Post'
import { PostEditorCreate, PostEditorTag, PostImageCreate, PostImageTag } from './Tag'

interface PostTextEditorProps {
  textEditors: string[]
  textPost: PostContent & PostAnchors & PostFootNotes
  onSelectionChange(focused: boolean, selection: string): void
  onTextChange(text: string): void
}

function PostEditorWrapper(props: { children: React.ReactNode }) {
  return (
    <main className="h-auto max-w-6xl mx-auto lg:px-12 md:px-8 sm:px-6 px-4 pt-6 space-y-2 my-8">{props.children}</main>
  )
}

function PostEditorLoading() {
  return (
    <article className="animate-pulse space-y-2 pt-8 pb-4">
      <h2 className="rounded-full mx-2 h-8 bg-gray-200 w-54 mb-5"></h2>
      <p className="rounded-full mx-2 h-4 bg-gray-200"></p>
      <p className="rounded-full mx-2 h-4 bg-gray-200"></p>
      <p className="rounded-full mx-2 h-4 bg-gray-200"></p>
      <p className="rounded-full mx-2 h-4 bg-gray-200 w-1/3"></p>
    </article>
  )
}

function PostTitleEditor(props: { initId: number; isDraft: boolean; onUpdateId(id: number | null): void }) {
  const [dateString, setDateString] = React.useState(toCSTDateString(props.initId))

  const [idString, setIdString] = React.useState('' + props.initId)

  const onIdInput = (idString: string) => {
    if (/^[1-9][0-9]*$/.test(idString)) {
      const id = parseInt(idString),
        dateString = toCSTDateString(id)
      setDateString(dateString)
      setIdString(idString)
      props.onUpdateId(id)
    } else {
      setDateString('????????????????')
      setIdString(idString)
      props.onUpdateId(null)
    }
  }

  return (
    <h2 className="text-2xl font-semibold pt-1">
      <span className="px-2 rounded-2xl bg-orange-200">
        #
        <ContentEditable
          tagName="span"
          disabled={false}
          className="outline-none"
          html={idString}
          onChange={(e) => onIdInput(e.currentTarget.innerText)}
        />
      </span>
      <span className="inline-block transform -translate-x-2">（{dateString}）</span>
    </h2>
  )
}

function PostFootnotesEditor(props: {
  footnotes: Record<string, string>
  onFootnotesChange(footnotes: Record<string, string>): void
}) {
  const footnotes = React.useMemo(
    () =>
      Array.from(
        Object.keys(props.footnotes).sort((a, b) => {
          const regex = /\[(\d+)\]/
          return parseInt(a.match(regex)?.[1] ?? '0', 10) - parseInt(b.match(regex)?.[1] ?? '0', 10)
        }),
        (footnoteNum) => ({ number: footnoteNum, content: props.footnotes[footnoteNum] })
      ),
    [props.footnotes]
  )

  const onFootnoteInput = (footnoteNum: string, content: string) => {
    const newFootnotes = footnotes.reduce((obj, item) => {
      obj[item.number] = item.number === footnoteNum ? content : item.content
      return obj
    }, {} as Record<string, string>)
    props.onFootnotesChange(newFootnotes)
  }

  return (
    <ul className="space-y-1">
      {footnotes.map((footnote) => (
        <li key={footnote.number} className="px-2 py-1 bg-orange-200 rounded-2xl">
          <sup>{footnote.number}</sup>
          <ContentEditable
            tagName="span"
            className="ml-1 px-1 py-0.5 bg-orange-100 empty:after:content-[attr(placeholder)] empty:after:inline empty:after:text-gray-400"
            html={footnote.content}
            placeholder="脚注内容"
            onChange={(e) => onFootnoteInput(footnote.number, e.currentTarget.innerText)}
          />
        </li>
      ))}
    </ul>
  )
}

function PostTextEditor(props: PostTextEditorProps) {
  const renderer = React.useMemo(() => createRenderer(PostArticleLink), [])

  const textRef = React.useRef<HTMLElement | null>(null)

  const lastSelectionRef = React.useRef('')

  const editors = props.textEditors

  const post = props.textPost

  React.useEffect(() => {
    const callback = () => {
      const selection = window.getSelection()
      const isFocused = document.activeElement === textRef.current
      if (isFocused) {
        const newSelection = renderer.fromSelectionToText(selection)
        props.onSelectionChange(true, newSelection)
        lastSelectionRef.current = newSelection
      } else {
        props.onSelectionChange(false, lastSelectionRef.current)
      }
    }
    document.addEventListener('selectionchange', callback)
    return () => document.removeEventListener('selectionchange', callback)
  }, [])

  return (
    <p
      onClick={() => textRef.current?.focus()}
      className="rounded-2xl px-2 py-1 break-words text-justify whitespace-pre-wrap bg-orange-200 min-h-8"
    >
      <ContentEditable
        tagName="span"
        disabled={false}
        innerRef={textRef}
        className="outline-none"
        html={renderer.fromTextToHtml(post.text, post, post).innerHTML}
        onChange={(e) => props.onTextChange(renderer.fromHtmlToText(e.currentTarget))}
      />
      <span>{editors.length > 0 ? `【本期编辑：${editors.join('，')}】` : ''}</span>
    </p>
  )
}

function PostEditArticle(props: { id: number; initPost: Post | Draft }) {
  const [id, { submitId, submitPost, uploadImage, removeImage }] = useEditPost(props.id)

  const [isWebp, toggleWebp] = React.useState<boolean | null>(null)

  const [post, setPost] = React.useState({ ...props.initPost })

  React.useEffect(() => (supportsWebP.then((useWebp) => toggleWebp(useWebp)), undefined), [])

  const [textEditorState, setTextEditorState] = React.useState({
    focus: false,
    selection: '',
  })

  const onDeleteEditor = (editor: string) => {
    setPost({ ...post, editors: post.editors.filter((e) => e !== editor) })
  }

  const onAddEditor = (editor: string) => {
    if (post.editors.indexOf(editor) < 0) {
      setPost({ ...post, editors: [...post.editors, editor] })
    }
  }

  const onUploadImage = async (blob: Blob) => {
    const image = await uploadImage(blob)
    setPost({ ...post, images: [...post.images, image] })
  }

  const onDeleteImage = async (id: string, detached: boolean) => {
    setPost({ ...post, images: post.images.filter((i) => i.id !== id) })
    if (detached) {
      await removeImage(id)
    }
  }

  const onAddLink = () => {
    const anchor = textEditorState.selection
    if (anchor.length > 0 && post.anchors.indexOf(anchor) < 0) {
      setPost({ ...post, anchors: [...post.anchors, anchor] })
    }
  }

  const onClearLink = () => {
    setPost({ ...post, anchors: [] })
  }

  const onAddFootnote = () => {
    const footnote = textEditorState.selection
    if (footnote.length > 0 && post.footnotes[footnote] == null) {
      setPost({ ...post, footnotes: { ...post.footnotes, [footnote]: '' } })
    }
  }

  const onSubmit = () => {
    submitPost(post)
  }

  return (
    <article className="space-y-3 pt-4 relative">
      <div className="text-2xl font-semibold absolute right-0 pt-1 space-x-2">
        <Button onClick={onAddLink} disabled={!textEditorState.focus || !textEditorState.selection}>
          将选中内容设为链接
        </Button>
        <Button onClick={onClearLink} disabled={post.anchors.length === 0}>
          清空所有链接
        </Button>
        <Button
          disabled={
            !textEditorState.focus || !textEditorState.selection || !/^\[[1-9]\d*\]$/.test(textEditorState.selection)
          }
          onClick={onAddFootnote}
        >
          将选中内容设为脚注
        </Button>
        <Button onClick={onSubmit} disabled={id === null || post.text.trim().length === 0}>
          提交
        </Button>
      </div>
      <PostTitleEditor initId={props.id} onUpdateId={submitId} isDraft={post.type === 'draft'} />
      <PostTextEditor
        textEditors={post.editors}
        textPost={{ ...post, anchor_urls: [] }}
        onTextChange={(text) => setPost({ ...post, text })}
        onSelectionChange={(focus, selection) => setTextEditorState({ focus, selection })}
      />
      <PostFootnotesEditor
        footnotes={post.footnotes}
        onFootnotesChange={(footnotes) => setPost({ ...post, footnotes })}
      />
      <div className="flex flex-row flex-wrap children:mr-3 children:mb-3">
        {post.editors.sort().map((editor, i) => (
          <PostEditorTag key={i} editor={editor} onDelete={onDeleteEditor} />
        ))}
        <PostEditorCreate onAdd={onAddEditor} exists={(editor) => post.editors.indexOf(editor) >= 0} />
        {post.images.map((image, i) => (
          <PostImageTag
            key={i}
            id={image.id}
            size={image.size}
            detached={image.detached}
            onDelete={onDeleteImage}
            url={isWebp ? image.webp_url : image.png_url}
          />
        ))}
        <PostImageCreate onUpload={onUploadImage} />
      </div>
    </article>
  )
}

export function MainEdit() {
  const params = Router.useParams<{ id: string }>()

  const id = parseInt(params.id)

  const [posts] = usePost(id)

  if (posts === null) {
    return (
      <PostEditorWrapper>
        <PostEditorLoading />
      </PostEditorWrapper>
    )
  }
  const post = posts[0] ?? createPost(),
    isRevision = post.type === 'revision'
  const initPost: Post | Draft = isRevision ? { ...post, editors: [], type: 'draft' } : post
  return (
    <PostEditorWrapper>
      <PostEditArticle id={id} initPost={initPost} />
    </PostEditorWrapper>
  )
}
